import React from 'react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BookOpen, Users, Sparkles, Target, Globe, Heart } from 'lucide-react';
export function About() {
  const pillars = [{
    icon: <BookOpen className="text-[#D4AF37]" size={24} data-id="element-183" />,
    title: 'Passion for Education',
    desc: 'We believe knowledge is the ultimate luxury. Our sessions deepen your understanding of viticulture without the jargon.'
  }, {
    icon: <Sparkles className="text-[#D4AF37]" size={24} data-id="element-184" />,
    title: 'Approachable Luxury',
    desc: "Luxury shouldn't be intimidating. We curate warm, inviting atmospheres where discovery is the primary focus."
  }, {
    icon: <Users className="text-[#D4AF37]" size={24} data-id="element-185" />,
    title: 'Culture & Community',
    desc: 'Every bottle has a story. We pair world-class hospitality with rich narratives to create holistic cultural experiences.'
  }];
  const visionItems = [{
    title: 'Culinary Partnerships',
    desc: 'Collaborating with world-renowned chefs for immersive dining.'
  }, {
    title: 'Corporate Experiences',
    desc: 'Bespoke team-building and leadership retreats for global teams.'
  }, {
    title: 'Nonprofit Events',
    desc: 'Leveraging wine culture to support community-driven social impact.'
  }, {
    title: 'Community Engagement',
    desc: 'Fostering a local ecosystem through workshops and social clubs.'
  }, {
    title: 'Winery Collaborations',
    desc: 'Partnering with boutique estates to highlight rare vintages.'
  }, {
    title: 'Travel & Media',
    desc: 'Bringing the valley to the world through editorial storytelling.'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="min-h-screen bg-white" data-id="element-186">
      <Header data-id="element-187" />

      <main className="pt-32 pb-20" data-id="element-188">
        {/* Our Story Section */}
        <section className="max-w-7xl mx-auto px-6 mb-24" data-id="element-189">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-190">
            <div className="order-2 lg:order-1" data-id="element-191">
              <span className="section-overline" data-id="element-192">Our Story</span>
              <h1 className="text-[#4A0E0E] mb-6 text-4xl lg:text-5xl leading-tight" data-id="element-193">
                About Cork Dorx
              </h1>
              <div className="space-y-5 text-[#2C2C2C]/70 text-base leading-relaxed max-w-xl" data-id="element-194">
                <p data-id="element-195">
                  Cork Dorx was founded from a passion for wine, education, and
                  community. After more than 14 years in the industry, I
                  recognized a gap: wine education often felt intimidating,
                  exclusive, or inaccessible.
                </p>
                <p data-id="element-196">
                  I also rarely saw wine education intentionally promoted within
                  communities of color, despite the growing influence of diverse
                  enthusiasts. Driven to change that narrative, I created Cork
                  Dorx—a company dedicated to making wine approachable,
                  engaging, and enjoyable for everyone.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2" data-id="element-197">
              <div className="aspect-[16/10] lg:aspect-[4/3] overflow-hidden rounded-2xl shadow-xl" data-id="element-198">
                <img src="https://cdn.magicpatterns.com/uploads/4cH5pTAV7i4ZDdJEUQP5u9/Color_Leaning_Arms_Crossed_Smile.jpg" alt="Founder of Cork Dorx" className="w-full h-full object-cover object-top" data-id="element-199" />
              </div>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="bg-[#FDFCF0] py-20 px-6 mb-24" data-id="element-200">
          <div className="max-w-3xl mx-auto text-center" data-id="element-201">
            <h2 className="text-[#4A0E0E] mb-8 text-3xl" data-id="element-202">Our Mission</h2>
            <p className="text-lg md:text-xl text-[#2C2C2C]/80 font-serif italic leading-relaxed" data-id="element-203">
              "My goal is to remove the intimidation factor and help people
              discover wine in a way that feels welcoming, relatable, and fun.
              We encourage people to explore new wines, expand their palates,
              and gain confidence in their knowledge."
            </p>
          </div>
        </section>

        {/* Grains & Grapes MD */}
        <section className="max-w-7xl mx-auto px-6 mb-24" data-id="element-204">
          <div className="bg-[#4A0E0E] text-white p-10 md:p-16 rounded-3xl relative overflow-hidden flex flex-col md:flex-row items-center gap-10" data-id="element-205">
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#D4AF37] opacity-10 rounded-full -mr-24 -mt-24" data-id="element-206" />
            <div className="relative z-10 flex-1" data-id="element-207">
              <span className="text-[#D4AF37] font-bold uppercase tracking-widest text-[10px] mb-4 block" data-id="element-208">
                Grains & Grapes MD
              </span>
              <h2 className="text-white text-2xl md:text-3xl font-serif mb-0" data-id="element-209">
                A thriving community bringing people together through shared
                experiences centered around wine, beer, spirits, and connection.
              </h2>
            </div>
            <div className="relative z-10" data-id="element-210">
              <button className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-white transition-all" data-id="element-211">
                Join Community
              </button>
            </div>
          </div>
        </section>

        {/* Core Pillars */}
        <section className="max-w-7xl mx-auto px-6 mb-24" data-id="element-212">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-213">
            {pillars.map((pillar, idx) => <div key={idx} className="p-8 rounded-2xl border border-[#E6D5B8]/30 hover:border-[#D4AF37]/50 transition-colors group" data-id="element-214">
                <div className="mb-6" data-id="element-215">{pillar.icon}</div>
                <h3 className="text-xl font-serif text-[#4A0E0E] mb-4" data-id="element-216">
                  {pillar.title}
                </h3>
                <p className="text-[#2C2C2C]/70 text-sm leading-relaxed font-light" data-id="element-217">
                  {pillar.desc}
                </p>
              </div>)}
          </div>
        </section>

        {/* Future Vision */}
        <section className="bg-[#FDFCF0] py-20 px-6 mb-24" data-id="element-218">
          <div className="max-w-7xl mx-auto" data-id="element-219">
            <div className="text-center mb-16" data-id="element-220">
              <span className="section-overline" data-id="element-221">The Horizon</span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#4A0E0E] mb-6" data-id="element-222">
                Vision for the Future
              </h2>
              <p className="text-[#2C2C2C]/60 max-w-2xl mx-auto text-base" data-id="element-223">
                I want Cork Dorx to serve as a resource, guide, and community
                that inspires curiosity, learning, and connection, one glass at
                a time.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-224">
              {visionItems.map((item, idx) => <div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-[#E6D5B8]/20" data-id="element-225">
                  <h4 className="text-lg font-serif text-[#4A0E0E] mb-3" data-id="element-226">
                    {item.title}
                  </h4>
                  <p className="text-[#2C2C2C]/60 font-light leading-relaxed text-xs" data-id="element-227">
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center px-6" data-id="element-228">
          <div className="max-w-2xl mx-auto" data-id="element-229">
            <h2 className="text-3xl font-serif text-[#4A0E0E] mb-8" data-id="element-230">
              Experience the Story Firsthand
            </h2>
            <button className="bg-[#4A0E0E] text-white px-10 py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-[#D4AF37] transition-all duration-500 shadow-lg" data-id="element-231">
              Book a Tasting with Us
            </button>
          </div>
        </section>
      </main>

      <Footer data-id="element-232" />
    </motion.div>;
}